import { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, Folder, FileText, Film, Image as ImageIcon, Download, HardDriveDownload,
  ChevronRight, Home, ExternalLink, CloudOff, RefreshCw, Upload, FolderPlus, Palette, Search, X, Share2, CheckSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAccount } from "@/context/AccountContext";
import { fmtSize } from "@/pages/Dateien";

function ItemIcon({ item }) {
  if (item.is_folder) return <Folder className="h-5 w-5 text-sky-600 shrink-0" />;
  if ((item.mime || "").startsWith("image/")) return <ImageIcon className="h-5 w-5 text-emerald-600 shrink-0" />;
  if ((item.mime || "").startsWith("video/")) return <Film className="h-5 w-5 text-violet-600 shrink-0" />;
  return <FileText className="h-5 w-5 text-neutral-400 shrink-0" />;
}

export function SharePointBrowser({ projectId = "all", projects = [], rootPath = "", rootLabel = "" }) {
  const [status, setStatus] = useState(null);
  const [drives, setDrives] = useState([]);
  const [drive, setDrive] = useState(null);
  const [crumbs, setCrumbs] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [brandImport, setBrandImport] = useState(null);
  const [videoCats, setVideoCats] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkSub, setBulkSub] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.canShare === "function";
  const { currentKey, current } = useAccount() || {};
  const fileRef = useRef(null);

  const loadVideoCats = () => {
    if (currentKey && videoCats.length === 0)
      api.get("/brand/video-categories", { params: { account: currentKey } })
        .then(({ data }) => setVideoCats([...data.defaults, ...data.custom.map((c) => c.name)]))
        .catch(() => {});
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    loadVideoCats();
  };

  const cleanAssetName = (fn) => fn.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();

  const pickedFiles = () => items.filter((i) => selected.has(i.id));

  const selectAllFiles = () => {
    const files = items.filter((i) => !i.is_folder);
    setSelected((prev) => {
      const allSelected = files.length > 0 && files.every((f) => prev.has(f.id));
      if (allSelected) return new Set();
      return new Set(files.map((f) => f.id));
    });
  };

  const zipDownload = async () => {
    const picked = pickedFiles();
    if (!picked.length) return;
    const totalSize = picked.reduce((s, i) => s + (i.size || 0), 0);
    if (totalSize > 2 * 1024 * 1024 * 1024) {
      toast.error("Auswahl zu groß (max. 2 GB) — bitte weniger Dateien oder kleinere Ordner wählen");
      return;
    }
    setZipBusy(true);
    try {
      const { data } = await api.post(
        "/sharepoint/download-zip",
        { drive_id: drive.id, item_ids: picked.map((i) => i.id), account_key: currentKey },
        { timeout: 900000 },
      );
      const token = localStorage.getItem("rl_token");
      const a = document.createElement("a");
      a.href = `${process.env.REACT_APP_BACKEND_URL}/api/sharepoint/zip/${data.zip_id}?token=${encodeURIComponent(token)}`;
      a.download = "sharepoint-dateien.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (data.skipped > 0 || data.truncated) {
        toast.warning(`ZIP mit ${data.count} Dateien wird heruntergeladen — ${data.skipped > 0 ? `${data.skipped} Datei${data.skipped === 1 ? "" : "en"} übersprungen` : "Auswahl wurde gekürzt (max. 300 Dateien)"}`);
      } else {
        toast.success(`ZIP mit ${data.count} Datei${data.count === 1 ? "" : "en"} wird heruntergeladen ✅`);
      }
      setSelected(new Set());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const clean = typeof detail === "string" && detail.length < 200 && !detail.includes("<") && !/cloudflare|origin web server/i.test(detail);
      toast.error(clean ? detail : "ZIP-Download fehlgeschlagen — Auswahl evtl. zu groß (max. 2 GB)");
    } finally {
      setZipBusy(false);
    }
  };

  const shareToDevice = async () => {
    const picked = pickedFiles().filter((i) => !i.is_folder);
    if (!picked.length) {
      toast.error("Ordner können nur »Als ZIP« gespeichert werden");
      return;
    }
    setShareBusy(true);
    try {
      const files = [];
      for (const it of picked) {
        const resp = await api.get("/sharepoint/file", {
          params: { drive_id: drive.id, item_id: it.id, account: currentKey },
          responseType: "blob", timeout: 600000,
        });
        files.push(new File([resp.data], it.name, { type: resp.data.type || it.mime || "application/octet-stream" }));
      }
      if (!files.length || !navigator.canShare({ files })) {
        toast.error("Direktes Speichern wird hier nicht unterstützt — bitte »Als ZIP« nutzen");
        return;
      }
      await navigator.share({ files });
      toast.success("Im Teilen-Menü »Sichern« wählen — fertig ✅");
      setSelected(new Set());
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Speichern nicht möglich — bitte »Als ZIP« nutzen");
    } finally {
      setShareBusy(false);
    }
  };

  const bulkImportToBrand = async () => {
    const picked = items.filter((i) => selected.has(i.id));
    if (!picked.length) return;
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    for (const it of picked) {
      setBulkProgress(`${ok + failed + 1}/${picked.length}`);
      try {
        const { data } = await api.post("/sharepoint/import", { drive_id: drive.id, item_id: it.id, project_id: "", account_key: currentKey });
        await api.post("/brand/assets", {
          account_key: currentKey, category: "video", name: cleanAssetName(it.name),
          media_url: data.url, file_kind: data.kind || "video", subcategory: bulkSub,
        });
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    setBulkProgress("");
    setSelected(new Set());
    if (ok) toast.success(`${ok} Video${ok === 1 ? "" : "s"} im Brand Hub gespeichert (${bulkSub || "Sonstiges"}) ✅`);
    if (failed) toast.error(`${failed} Video${failed === 1 ? "" : "s"} fehlgeschlagen`);
  };

  const openBrandImport = (item) => {
    setBrandImport({ item, name: cleanAssetName(item.name), subcategory: "", saving: false });
    loadVideoCats();
  };

  const saveBrandImport = async () => {
    if (!brandImport?.name.trim()) { toast.error("Bitte einen Namen angeben"); return; }
    setBrandImport((s) => ({ ...s, saving: true }));
    try {
      const { data } = await api.post("/sharepoint/import", { drive_id: drive.id, item_id: brandImport.item.id, project_id: "", account_key: currentKey });
      await api.post("/brand/assets", {
        account_key: currentKey, category: "video", name: brandImport.name.trim(),
        media_url: data.url, file_kind: data.kind || "video", subcategory: brandImport.subcategory,
      });
      toast.success(`„${brandImport.name.trim()}" im Brand Hub gespeichert (${brandImport.subcategory || "Sonstiges"}) ✅`);
      setBrandImport(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Übernahme fehlgeschlagen");
      setBrandImport((s) => (s ? { ...s, saving: false } : s));
    }
  };

  const currentFolderId = () => (crumbs.length ? crumbs[crumbs.length - 1].id : null);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const tooBig = files.filter((f) => f.size > 500 * 1024 * 1024);
    if (tooBig.length) { toast.error(`Maximal 500 MB pro Datei (${tooBig.map((f) => f.name).join(", ")})`); return; }
    setUploadBusy(true);
    const CHUNK = 4 * 1024 * 1024;
    let ok = 0;
    const failed = [];
    for (let n = 0; n < files.length; n++) {
      const f = files[n];
      setUploadInfo({ idx: n + 1, total: files.length, pct: 0, name: f.name });
      try {
        const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, "0")).join("");
        const totalChunks = Math.max(1, Math.ceil(f.size / CHUNK));
        for (let i = 0; i < totalChunks; i++) {
          const fd = new FormData();
          fd.append("upload_id", uploadId);
          fd.append("index", i);
          fd.append("account_key", currentKey || "");
          fd.append("chunk", f.slice(i * CHUNK, (i + 1) * CHUNK));
          await api.post("/sharepoint/upload-chunk", fd, { timeout: 120000 });
          setUploadInfo({ idx: n + 1, total: files.length, pct: Math.round(((i + 1) / totalChunks) * 95), name: f.name });
        }
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("filename", f.name);
        fd.append("total", totalChunks);
        fd.append("drive_id", drive.id);
        fd.append("parent_id", currentFolderId() || "");
        fd.append("account_key", currentKey || "");
        await api.post("/sharepoint/upload-complete", fd, { timeout: 600000 });
        ok += 1;
      } catch (err) {
        failed.push(f.name);
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `„${f.name}" fehlgeschlagen`);
      }
    }
    if (ok > 0) toast.success(ok === 1 ? `„${files[0].name}" nach SharePoint hochgeladen ✅` : `${ok} von ${files.length} Dateien nach SharePoint hochgeladen ✅`);
    browse(drive.id, currentFolderId());
    setUploadBusy(false);
    setUploadInfo(null);
  };

  const newFolder = async () => {
    const name = window.prompt("Name des neuen Ordners:");
    if (!name?.trim()) return;
    try {
      await api.post("/sharepoint/folders", { drive_id: drive.id, parent_id: currentFolderId() || "", name, account_key: currentKey });
      toast.success(`Ordner „${name.trim()}" angelegt ✅`);
      browse(drive.id, currentFolderId());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Ordner konnte nicht angelegt werden");
    }
  };

  const canPreview = (item) => (item.mime || "").startsWith("image/") || (item.mime || "").startsWith("video/");

  const openPreview = async (item) => {
    setPreview({ item, url: null });
    try {
      const { data } = await api.get("/sharepoint/download-url", { params: { drive_id: drive.id, item_id: item.id, account: currentKey } });
      setPreview({ item, url: data.url });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Vorschau fehlgeschlagen");
      setPreview(null);
    }
  };

  const browse = useCallback(async (driveId, itemId = null) => {
    setLoading(true);
    try {
      const params = { drive_id: driveId, account: currentKey };
      if (itemId) params.item_id = itemId;
      else if (rootPath) params.path = rootPath;
      const { data } = await api.get("/sharepoint/browse", { params });
      setItems(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "SharePoint-Anfrage fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [rootPath, currentKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: st } = await api.get("/sharepoint/status", { params: { account: currentKey } });
        if (cancelled) return;
        setStatus(st);
        if (!st.connected) { setLoading(false); return; }
        const { data: drv } = await api.get("/sharepoint/drives", { params: { account: currentKey } });
        if (cancelled) return;
        setDrives(drv);
        if (drv.length > 0) {
          setDrive(drv[0]);
          await browse(drv[0].id);
        } else {
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setStatus({ configured: true, connected: false }); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [browse, currentKey]);

  const runSearch = async () => {
    if (!query.trim() || !drive) return;
    setLoading(true);
    setSearchMode(true);
    try {
      const { data } = await api.get("/sharepoint/search", { params: { drive_id: drive.id, q: query.trim(), account: currentKey } });
      setItems(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Suche fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setSearchMode(false);
    browse(drive?.id, currentFolderId());
  };

  const openFolder = (item) => {
    if (searchMode) { setSearchMode(false); setQuery(""); setCrumbs([{ id: item.id, name: item.name }]); }
    else setCrumbs((c) => [...c, { id: item.id, name: item.name }]);
    browse(drive.id, item.id);
  };

  const goCrumb = (idx) => {
    if (idx < 0) {
      setCrumbs([]);
      browse(drive.id);
    } else {
      const next = crumbs.slice(0, idx + 1);
      setCrumbs(next);
      browse(drive.id, next[next.length - 1].id);
    }
  };

  const download = async (item) => {
    setBusy(`dl-${item.id}`);
    try {
      const { data } = await api.get("/sharepoint/download-url", { params: { drive_id: drive.id, item_id: item.id, account: currentKey } });
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download fehlgeschlagen");
    } finally {
      setBusy("");
    }
  };

  const importFile = async (item) => {
    setBusy(`imp-${item.id}`);
    try {
      const pid = projectId !== "all" ? projectId : "";
      await api.post("/sharepoint/import", { drive_id: drive.id, item_id: item.id, project_id: pid, account_key: currentKey });
      const pname = pid ? projects.find((p) => p.id === pid)?.name : "";
      toast.success(`„${item.name}" in die Ablage importiert${pname ? ` (${pname})` : ""} ✅`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import fehlgeschlagen");
    } finally {
      setBusy("");
    }
  };

  if (status && (!status.configured || !status.connected)) {
    return (
      <div className="text-center py-12 text-neutral-400" data-testid="sharepoint-offline">
        <CloudOff className="h-8 w-8 mx-auto mb-3" />
        <p className="text-sm font-medium">
          {status.configured ? "SharePoint ist derzeit nicht verbunden." : `Für ${current?.name || currentKey} ist noch keine SharePoint-Quelle hinterlegt.`}
        </p>
        {!status.configured && <p className="text-xs mt-1">Unter Einstellungen & Integrationen kannst du die kanalbezogene Quelle eintragen.</p>}
        {status.error && <p className="text-xs mt-1">{status.error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="sharepoint-browser">
      {status?.connected && (
        <div className="flex items-center justify-between gap-2 bg-sky-50 border border-sky-200 rounded-xl px-3.5 py-2.5">
          <div className="min-w-0">
            <p className="text-xs font-bold text-sky-800 truncate" data-testid="sharepoint-site-name">
              Microsoft 365 · {status.site_name}
            </p>
            {drives.length > 1 && (
              <select
                data-testid="sharepoint-drive-select"
                value={drive?.id || ""}
                onChange={(e) => {
                  const d = drives.find((x) => x.id === e.target.value);
                  setDrive(d); setCrumbs([]); browse(d.id);
                }}
                className="mt-1 text-xs bg-white border border-sky-200 rounded-lg px-2 py-1"
              >
                {drives.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>
          <a href={status.web_url} target="_blank" rel="noreferrer" data-testid="sharepoint-open-web"
            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:text-sky-900">
            <ExternalLink className="h-3 w-3" /> In SharePoint öffnen
          </a>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
          <input
            data-testid="sharepoint-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); if (e.key === "Escape" && searchMode) clearSearch(); }}
            placeholder="In SharePoint suchen (Dateiname)…"
            className="w-full h-9 pl-9 pr-8 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-400"
          />
          {(query || searchMode) && (
            <button data-testid="sharepoint-search-clear" onClick={clearSearch} title="Suche zurücksetzen"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded flex items-center justify-center text-neutral-400 hover:text-neutral-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          data-testid="sharepoint-search-btn"
          onClick={runSearch}
          disabled={!query.trim() || loading}
          className="h-9 px-3.5 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition disabled:opacity-50"
        >
          Suchen
        </button>
      </div>

      {searchMode && !loading && (
        <p data-testid="sharepoint-search-info" className="text-[12px] text-neutral-500">
          {items.length} Treffer für „{query}“ in der gesamten Bibliothek
        </p>
      )}

      <div className="flex items-center gap-1 text-xs text-neutral-500 flex-wrap" data-testid="sharepoint-breadcrumbs">
        <button onClick={() => goCrumb(-1)} data-testid="sharepoint-crumb-root"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-neutral-100 font-semibold">
          <Home className="h-3.5 w-3.5" /> {rootLabel || rootPath || drive?.name || "Dokumente"}
        </button>
        {crumbs.map((c, i) => (
          <span key={c.id} className="inline-flex items-center gap-1">
            <ChevronRight className="h-3 w-3 text-neutral-300" />
            <button onClick={() => goCrumb(i)} className="px-2 py-1 rounded-lg hover:bg-neutral-100 font-medium truncate max-w-[140px]">
              {c.name}
            </button>
          </span>
        ))}
        <input ref={fileRef} type="file" multiple className="hidden" onChange={onUpload} />
        {!rootPath && (
          <>
            <button onClick={() => fileRef.current?.click()} disabled={uploadBusy}
              data-testid="sharepoint-upload-btn" title="Nach SharePoint hochladen"
              className="ml-auto h-7 px-2 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 flex items-center gap-1 text-[11px] font-bold disabled:opacity-50">
              {uploadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{uploadBusy && uploadInfo ? `${uploadInfo.total > 1 ? `${uploadInfo.idx}/${uploadInfo.total} · ` : ""}${uploadInfo.pct}%` : "Hochladen"}</span>
              {uploadBusy && uploadInfo && <span className="sm:hidden" data-testid="sharepoint-upload-pct">{uploadInfo.total > 1 ? `${uploadInfo.idx}/${uploadInfo.total} · ` : ""}{uploadInfo.pct}%</span>}
            </button>
            <button onClick={newFolder} data-testid="sharepoint-newfolder-btn" title="Neuer Ordner"
              className="h-7 w-7 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-400">
              <FolderPlus className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        <button onClick={selectAllFiles}
          data-testid="sharepoint-select-all" title="Alle Dateien im Ordner aus-/abwählen"
          className="h-7 px-2.5 rounded-lg hover:bg-neutral-100 flex items-center gap-1 text-[11px] font-semibold text-neutral-500">
          <CheckSquare className="h-3.5 w-3.5" /> Alle
        </button>
        <button onClick={() => browse(drive?.id, crumbs.length ? crumbs[crumbs.length - 1].id : null)}
          data-testid="sharepoint-refresh" title="Aktualisieren"
          className={`h-7 w-7 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-400 ${rootPath ? "ml-auto" : ""}`}>
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex items-center gap-2 flex-wrap bg-violet-50 border border-violet-200 rounded-xl px-3.5 py-2.5" data-testid="sharepoint-bulk-bar">
          <span className="text-[12px] font-bold text-violet-800">
            {selected.size} ausgewählt
          </span>
          {canShare && (
            <button
              data-testid="sharepoint-bulk-share"
              onClick={shareToDevice}
              disabled={shareBusy || zipBusy || bulkBusy}
              className="h-8 px-3 rounded-lg bg-white border border-violet-300 text-violet-700 text-[12px] font-bold hover:bg-violet-100 flex items-center gap-1.5 disabled:opacity-60"
            >
              {shareBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
              {shareBusy ? "Lädt…" : "In Fotos/Dateien sichern"}
            </button>
          )}
          <button
            data-testid="sharepoint-bulk-zip"
            onClick={zipDownload}
            disabled={zipBusy || shareBusy || bulkBusy}
            className="h-8 px-3 rounded-lg bg-violet-600 text-white text-[12px] font-bold hover:bg-violet-700 flex items-center gap-1.5 disabled:opacity-60"
          >
            {zipBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {zipBusy ? "Erstellt ZIP…" : "Als ZIP"}
          </button>
          {pickedFiles().length > 0 && pickedFiles().every((i) => (i.mime || "").startsWith("video/")) && (
            <>
              <select
                data-testid="sharepoint-bulk-subcategory"
                value={bulkSub}
                onChange={(e) => setBulkSub(e.target.value)}
                disabled={bulkBusy}
                className="h-8 px-2 rounded-md border border-violet-200 text-sm bg-white min-w-[120px]"
              >
                <option value="">Sonstiges</option>
                {videoCats.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                data-testid="sharepoint-bulk-save"
                onClick={bulkImportToBrand}
                disabled={bulkBusy || zipBusy || shareBusy}
                className="h-8 px-3 rounded-lg bg-violet-600 text-white text-[12px] font-bold hover:bg-violet-700 flex items-center gap-1.5 disabled:opacity-60"
              >
                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Palette className="h-3.5 w-3.5" />}
                {bulkBusy ? `Übernehme ${bulkProgress}…` : "In Brand Hub"}
              </button>
            </>
          )}
          <button
            data-testid="sharepoint-bulk-clear"
            onClick={() => setSelected(new Set())}
            disabled={bulkBusy}
            className="ml-auto h-8 w-8 rounded-lg hover:bg-violet-100 text-violet-400 hover:text-violet-700 text-sm"
            title="Auswahl aufheben"
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-12 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-10" data-testid="sharepoint-empty">
          {searchMode
            ? `Keine Treffer für „${query}".`
            : rootPath && crumbs.length === 0
            ? "Noch keine archivierten Medien — veröffentlichte Beiträge landen hier automatisch."
            : "Dieser Ordner ist leer."}
        </p>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
          {items.map((item) => (
            <div key={item.id} data-testid={`sharepoint-item-${item.id}`}
              className={`flex items-center gap-3 px-3.5 py-2.5 ${item.is_folder || canPreview(item) ? "cursor-pointer hover:bg-neutral-50" : ""}`}
              onClick={item.is_folder ? () => openFolder(item) : canPreview(item) ? () => openPreview(item) : undefined}>
              {(
                <input
                  type="checkbox"
                  data-testid={`sharepoint-select-${item.id}`}
                  checked={selected.has(item.id)}
                  disabled={bulkBusy}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(item.id)}
                  title={item.is_folder ? "Ordner komplett für ZIP-Download auswählen" : undefined}
                  className="h-4 w-4 shrink-0 accent-violet-600 cursor-pointer"
                />
              )}
              {item.thumb ? (
                <img src={item.thumb} alt="" loading="lazy" className="h-11 w-11 rounded-lg object-cover shrink-0 border border-neutral-100" />
              ) : (
                <ItemIcon item={item} />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-800 truncate">{item.name}</p>
                <p className="text-[11px] text-neutral-400">
                  {item.is_folder
                    ? `${item.child_count} ${item.child_count === 1 ? "Element" : "Elemente"}`
                    : fmtSize(item.size)}
                  {item.modified && ` · ${new Date(item.modified).toLocaleDateString("de-DE")}`}
                  {item.modified_by && ` · ${item.modified_by}`}
                </p>
              </div>
              {item.is_folder ? (
                <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0" />
              ) : (
                <div className="flex items-center gap-1 shrink-0">
                  {(item.mime || "").startsWith("video/") && (
                    <button data-testid={`sharepoint-brandhub-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); openBrandImport(item); }}
                      disabled={!!busy}
                      title="In Brand Hub übernehmen"
                      className="h-8 px-2.5 rounded-lg bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-40">
                      <Palette className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Brand Hub</span>
                    </button>
                  )}
                  <button data-testid={`sharepoint-import-${item.id}`}
                    onClick={(e) => { e.stopPropagation(); importFile(item); }}
                    disabled={!!busy}
                    title="In Ablage importieren"
                    className="h-8 px-2.5 rounded-lg bg-orange-500/10 text-orange-700 hover:bg-orange-500/20 flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-40">
                    {busy === `imp-${item.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <HardDriveDownload className="h-3.5 w-3.5" />}
                    <span className="hidden sm:inline">In Ablage</span>
                  </button>
                  <button data-testid={`sharepoint-download-${item.id}`}
                    onClick={(e) => { e.stopPropagation(); download(item); }}
                    disabled={!!busy}
                    title="Herunterladen"
                    className="h-8 w-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-500 disabled:opacity-40">
                    {busy === `dl-${item.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {brandImport && (
        <div className="fixed inset-0 z-[10008] flex items-center justify-center p-4" data-testid="sharepoint-brandhub-dialog">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !brandImport.saving && setBrandImport(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3.5">
            <div>
              <p className="text-sm font-bold text-neutral-900 flex items-center gap-2">
                <Palette className="h-4 w-4 text-violet-600" /> In Brand Hub übernehmen
              </p>
              <p className="text-[12px] text-neutral-400 mt-0.5 truncate">{brandImport.item.name}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Name</label>
              <input
                data-testid="sharepoint-brandhub-name"
                value={brandImport.name}
                onChange={(e) => setBrandImport((s) => ({ ...s, name: e.target.value }))}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Video-Kategorie</label>
              <select
                data-testid="sharepoint-brandhub-subcategory"
                value={brandImport.subcategory}
                onChange={(e) => setBrandImport((s) => ({ ...s, subcategory: e.target.value }))}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white"
              >
                <option value="">Sonstiges</option>
                {videoCats.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setBrandImport(null)} disabled={brandImport.saving}
                className="flex-1 text-sm font-medium px-4 py-2.5 rounded-lg border border-neutral-200 hover:bg-neutral-50">
                Abbrechen
              </button>
              <button data-testid="sharepoint-brandhub-save" onClick={saveBrandImport} disabled={brandImport.saving}
                className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-medium px-4 py-2.5 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50">
                {brandImport.saving && <Loader2 className="h-4 w-4 animate-spin" />} Übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[10007] flex items-center justify-center p-3 sm:p-8" data-testid="sharepoint-preview-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setPreview(null)} />
          <div className="relative max-w-4xl w-full max-h-full flex flex-col">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-white truncate">{preview.item.name}</p>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  data-testid="sharepoint-preview-import"
                  onClick={() => importFile(preview.item)}
                  className="h-9 px-3 rounded-lg bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 flex items-center gap-1.5"
                >
                  {busy === `imp-${preview.item.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <HardDriveDownload className="h-3.5 w-3.5" />} In Ablage
                </button>
                <button
                  data-testid="sharepoint-preview-download"
                  onClick={() => download(preview.item)}
                  className="h-9 w-9 rounded-lg bg-white/15 text-white hover:bg-white/25 flex items-center justify-center"
                  title="Herunterladen"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  data-testid="sharepoint-preview-close"
                  onClick={() => setPreview(null)}
                  className="h-9 w-9 rounded-lg bg-white/15 text-white hover:bg-white/25 flex items-center justify-center text-lg leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden bg-black/40 flex items-center justify-center min-h-[200px]">
              {!preview.url ? (
                <Loader2 className="h-7 w-7 animate-spin text-white/70 my-16" />
              ) : (preview.item.mime || "").startsWith("video/") ? (
                <video src={preview.url} controls autoPlay className="max-h-[72vh] w-full" data-testid="sharepoint-preview-video" />
              ) : (
                <img src={preview.url} alt={preview.item.name} className="max-h-[72vh] w-auto object-contain" data-testid="sharepoint-preview-image" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
